import axios from "axios";
import { fetchAuthSession } from "aws-amplify/auth";
import {
  refreshToken,
  isTokenExpiring,
  handleTokenExpiration,
} from "./tokenManager";
import { getConfig } from "common/config";
import {
  applyCustomerContextHeaders,
  attachCustomerHeaderOmissionInterceptor,
  clearCustomerContextHeaders,
} from "./CustomerContextHeaders";

const base_url = getConfig('REACT_APP_BASE_URL');

const httpClientPAdmin = axios.create({
  baseURL: base_url,
  responseType: "json",
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

httpClientPAdmin.interceptors.request.use(
  async (config) => {
    try {
      const session = await fetchAuthSession();

      if (!session.tokens || !session.tokens.accessToken) {
        return config;
      }

      if (isTokenExpiring(session.tokens.accessToken)) {
        const refreshed = await refreshToken();
        if (!refreshed) {
          return config;
        }
      }

      const token = session.tokens.accessToken.toString();
      config.headers.Authorization = `Bearer ${token}`;
      httpClientPAdmin.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } catch (error) {
      console.warn("Could not refresh token from Amplify session:", error);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

attachCustomerHeaderOmissionInterceptor(httpClientPAdmin);

httpClientPAdmin.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response && error.response.status === 401) {
      await handleTokenExpiration();
    }
    return Promise.reject(
      error instanceof Error ? error : Error("unknown error")
    );
  }
);

const setAuthTokenPAdmin = (token) => {
  if (!token) {
    delete httpClientPAdmin.defaults.headers.common.Authorization;
    clearCustomerContextHeaders();
    return;
  }
  httpClientPAdmin.defaults.headers.common.Authorization = `Bearer ${token}`;
};

const setTokenToHeaderPAdmin = (userid, token, customerCode) => {
  setAuthTokenPAdmin(token);
  if (!token) {
    return;
  }
  if (customerCode != null && String(customerCode).trim() !== "" && userid) {
    applyCustomerContextHeaders(userid, customerCode);
  } else {
    clearCustomerContextHeaders();
  }
};

const changeHeaderTokenPAdmin = (userId, customerCode) => {
  if (customerCode == null || String(customerCode).trim() === "") {
    clearCustomerContextHeaders();
    return;
  }
  const resolvedUserId =
    userId ?? httpClientPAdmin.defaults.headers.common["user-id"];
  applyCustomerContextHeaders(resolvedUserId, customerCode);
};

export {
  httpClientPAdmin,
  setAuthTokenPAdmin,
  setTokenToHeaderPAdmin,
  changeHeaderTokenPAdmin,
  base_url,
};
